/**
 * killSwitchApi.js
 *
 * Dedicated API service for the emergency kill switch HTTP calls.
 * Every function logs the outgoing request and response status to the browser
 * console. Errors are caught, logged with detail, and rethrown with a
 * consistent { message, detail } structure so callers can display meaningful
 * error messages without inspecting raw HTTP responses.
 */

import apiClient from './apiClient'

const BASE = '/admin/kill-switch';

/**
 * Normalise an axios error into a consistent shape.
 * @param {string} context - Human-readable description of the operation.
 * @param {Error} axiosError - The error thrown by apiClient.
 * @returns {Error}
 */
function buildError(context, axiosError) {
  const response = axiosError.response ?? null;
  const status = response ? response.status : 'network-error';
  const body = response ? response.data : axiosError;
  const detail =
    (body && body.detail) ||
    (body && body.message) ||
    (body instanceof Error ? body.message : axiosError.message) ||
    'Unknown error';
  const message = `[killSwitchApi] ${context} failed (HTTP ${status}): ${detail}`;
  console.error(message, { status, body });
  const err = new Error(message);
  err.detail = detail;
  err.status = status;
  return err;
}

/**
 * Fetch the current kill switch status.
 *
 * @returns {Promise<Object>} Object with `active` (boolean) and `activated_at` (string|null).
 */
export async function getKillSwitchStatus() {
  console.log(`[killSwitchApi] GET ${BASE}/status`);
  try {
    const response = await apiClient.get(`${BASE}/status`);
    console.log(`[killSwitchApi] GET ${BASE}/status → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    const status = err.response?.status ?? 'network-error';
    console.log(`[killSwitchApi] GET ${BASE}/status → HTTP ${status}`);
    throw buildError('getKillSwitchStatus', err);
  }
}

/**
 * Activate the kill switch and liquidate all open positions.
 *
 * @param {string} confirmation - Must be exactly "CONFIRM" (case-sensitive).
 * @returns {Promise<Object>} Object with `active`, `positions_liquidated`, and `orders`.
 */
export async function activateKillSwitch(confirmation) {
  console.log(`[killSwitchApi] POST ${BASE}/activate`, { confirmation });
  try {
    const response = await apiClient.post(`${BASE}/activate`, { confirmation });
    console.log(`[killSwitchApi] POST ${BASE}/activate → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    const status = err.response?.status ?? 'network-error';
    console.log(`[killSwitchApi] POST ${BASE}/activate → HTTP ${status}`);
    throw buildError('activateKillSwitch', err);
  }
}

/**
 * Deactivate the kill switch and clear the activation timestamp.
 *
 * @returns {Promise<Object>} Object with `active` set to false.
 */
export async function deactivateKillSwitch() {
  console.log(`[killSwitchApi] DELETE ${BASE}/deactivate`);
  try {
    const response = await apiClient.delete(`${BASE}/deactivate`);
    console.log(`[killSwitchApi] DELETE ${BASE}/deactivate → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    const status = err.response?.status ?? 'network-error';
    console.log(`[killSwitchApi] DELETE ${BASE}/deactivate → HTTP ${status}`);
    throw buildError('deactivateKillSwitch', err);
  }
}
